"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useCallback, useState } from "react"
import ChatInterface, { AGENT_WELCOME } from "@/components/chat/ChatInterface"
import { ChatSessionsProvider, useChatSessions } from "@/components/chat/ChatSessionsContext"
import { ChatLayout } from "@/layout/ChatLayout"
import {
  AgentCoreClientProvider,
  DEFAULT_AGENT_NAME,
  type AgentName,
} from "@/lib/agentcore-client"
import { deleteSession } from "@/services/sessionsService"
import { clearExtraction } from "@/services/extractionsService"
import { clearCollectionScope } from "@/services/collectionScopeService"
import { ExtractionsSidebar } from "@/components/chat/ExtractionsSidebar"
import { ExtractionsProvider, useExtractions } from "@/components/chat/ExtractionsContext"
import { DeepLiteratureMessagePresentationProvider } from "@/components/chat/DeepLiteratureMessagePresentation"
import { CollectionProvider } from "@/components/chat/CollectionContext"
import { CollectionSidebar } from "@/components/chat/CollectionSidebar"
import { InvocationContextProvider } from "@/components/chat/InvocationContext"
import { ReportPanelProvider, useReportPanel } from "@/components/chat/ReportPanelContext"
import { ReportSidePanel } from "@/components/chat/ReportSidePanel"
import { useAuth } from "@/hooks/useAuth"

type ChatPageProps = {
  agent?: AgentName
}

/**
 * Renders the ExtractionsSidebar with visibility rules based on screen size
 * and whether variables have been added:
 * - Mobile: hidden until at least one variable is added.
 * - Desktop: always visible (collapsed by default), expands when variables are added.
 *
 * Must be rendered inside an ExtractionsProvider so it can read the variables
 * list from context.
 *
 * @param isOpen - Whether the sidebar panel is currently expanded.
 * @param onToggle - Callback to toggle the panel open/closed.
 * @param sessionId - The active chat session UUID.
 */
function ExtractionsSidebarGate({
  isOpen,
  onToggle,
  sessionId,
}: {
  isOpen: boolean
  onToggle: () => void
  sessionId: string
}): React.JSX.Element | null {
  const { variables } = useExtractions()
  if (variables.length === 0) return null
  return <ExtractionsSidebar isOpen={isOpen} onToggle={onToggle} sessionId={sessionId} />
}

interface AgentChatLayoutProps {
  agent: AgentName
  children: React.ReactNode
  onNewChat: () => void
  canStartNewChat: boolean
  activeSessionId: string
  onSelectSession: (sessionId: string) => void
  onDeleteSession: (sessionId: string) => Promise<void>
  onActiveSessionGone: () => void
  sessionRefreshTrigger: number
  extractionsOpen: boolean
  onExtractionsToggle: () => void
  isSessionPersisted: boolean
}

/**
 * Renders ChatLayout with agent-appropriate panel configuration.
 *
 * This must be a top-level component (not defined inside ChatPage) so React
 * preserves its identity across re-renders and does not remount child trees.
 *
 * For the exploratory literature review agent it reads `useReportPanel()` to
 * determine whether the resizable split should be active — the resizable panel
 * only appears after the user clicks "Open full report" in a ReportWidget.
 *
 * @param props - All layout and panel configuration values from ChatPage.
 * @returns The rendered ChatLayout element.
 */
function AgentChatLayout({
  agent,
  children,
  onNewChat,
  canStartNewChat,
  activeSessionId,
  onSelectSession,
  onDeleteSession,
  onActiveSessionGone,
  sessionRefreshTrigger,
  extractionsOpen,
  onExtractionsToggle,
  isSessionPersisted,
}: AgentChatLayoutProps): React.JSX.Element {
  // useReportPanel() is safe to call here unconditionally — the default context
  // value returns isOpen: false when no ReportPanelProvider is in the tree.
  const { isOpen: reportIsOpen } = useReportPanel()
  const showResizable = agent === "exploratory-literature-review" && reportIsOpen

  return (
    <ChatLayout
      onNewChat={onNewChat}
      canStartNewChat={canStartNewChat}
      activeSessionId={activeSessionId}
      onSelectSession={onSelectSession}
      onDeleteSession={onDeleteSession}
      onActiveSessionGone={onActiveSessionGone}
      sessionRefreshTrigger={sessionRefreshTrigger}
      agent={agent}
      rightPanel={
        agent === "survey-variable-discovery" && isSessionPersisted ? (
          <ExtractionsSidebarGate
            isOpen={extractionsOpen}
            onToggle={onExtractionsToggle}
            sessionId={activeSessionId}
          />
        ) : agent === "deep-literature-review" ? (
          <CollectionSidebar />
        ) : undefined
      }
      resizablePanel={agent === "exploratory-literature-review" ? <ReportSidePanel /> : undefined}
      showResizablePanel={showResizable}
    >
      {children}
    </ChatLayout>
  )
}

/**
 * Inner chat page body rendered beneath {@link ChatSessionsProvider}, so it can
 * consume the per-session store via {@link useChatSessions}. Owns the session
 * lifecycle handlers that need to reset a session's store slice (new chat,
 * delete) while all persistent message/streaming state lives in the store.
 *
 * @param agent - The active agent id.
 * @param sessionId - The active session id.
 * @param setSessionId - Setter for the active session id.
 * @param isExistingSession - Whether the active session exists in the backend.
 * @param setIsExistingSession - Setter for the existing-session flag.
 * @param isSessionPersisted - Whether the active session is persisted.
 * @param setIsSessionPersisted - Setter for the persisted flag.
 * @param setSessionRefreshTrigger - Bumps the sidebar refresh trigger.
 * @param sessionRefreshTrigger - Current sidebar refresh trigger value.
 * @param pageError - Page-level error text.
 * @param setPageError - Setter for the page-level error.
 * @param configurationError - Persistent client configuration error.
 */
function ChatPageBody({
  agent,
  sessionId,
  setSessionId,
  isExistingSession,
  setIsExistingSession,
  isSessionPersisted,
  setIsSessionPersisted,
  setSessionRefreshTrigger,
  sessionRefreshTrigger,
  pageError,
  setPageError,
  configurationError,
}: {
  agent: AgentName
  sessionId: string
  setSessionId: (id: string) => void
  isExistingSession: boolean
  setIsExistingSession: (v: boolean) => void
  isSessionPersisted: boolean
  setIsSessionPersisted: (v: boolean) => void
  setSessionRefreshTrigger: React.Dispatch<React.SetStateAction<number>>
  sessionRefreshTrigger: number
  pageError: string | null
  setPageError: (v: string | null) => void
  configurationError: string | null
}): React.JSX.Element {
  const auth = useAuth()
  const store = useChatSessions()

  /** Whether the "New Chat" header button should be enabled. */
  const [canStartNewChat, setCanStartNewChat] = useState<boolean>(false)

  /**
   * Called when loading an existing session's history fails, or the session no
   * longer exists. Resets to a fresh session so the user isn't stuck.
   */
  const handleSessionLoadFailed = useCallback(() => {
    setSessionId(crypto.randomUUID())
    setIsExistingSession(false)
    setIsSessionPersisted(false)
  }, [setSessionId, setIsExistingSession, setIsSessionPersisted])

  /**
   * Start a new chat: mint a new session id and reset its (empty) store slice.
   * Existing background streams in other sessions are untouched and keep going.
   */
  const handleNewChat = useCallback(() => {
    const newId = crypto.randomUUID()
    store.resetSession(newId)
    setSessionId(newId)
    setIsExistingSession(false)
    setIsSessionPersisted(false)
    setPageError(null)
  }, [store, setSessionId, setIsExistingSession, setIsSessionPersisted, setPageError])

  /**
   * Select a previous session from the sidebar. Marks it existing so the
   * interface loads its history (unless the store already holds live messages).
   *
   * @param selectedSessionId - The session id chosen from the sidebar.
   */
  const handleSelectSession = useCallback(
    (selectedSessionId: string) => {
      setSessionId(selectedSessionId)
      setIsExistingSession(true)
      setIsSessionPersisted(true)
      setPageError(null)
    },
    [setSessionId, setIsExistingSession, setIsSessionPersisted, setPageError]
  )

  /**
   * Delete a session: remove it from the backend, refresh the sidebar, and if
   * it was active reset to a fresh chat. Also clears its store slice.
   *
   * @param deletedSessionId - The session id to delete.
   */
  const handleDeleteSession = useCallback(
    async (deletedSessionId: string) => {
      try {
        const token = await auth.getAccessToken()
        if (!token) return

        await deleteSession(deletedSessionId, token)

        // Clean up any extraction data. Best-effort — TTL cleans up otherwise.
        try {
          await clearExtraction(deletedSessionId, token)
        } catch (err) {
          console.warn("Failed to clear extraction (non-critical):", err)
        }

        // Clean up any saved deep-literature-review collection scope for this
        // session. Best-effort — the scope row's TTL cleans it up otherwise.
        try {
          await clearCollectionScope(deletedSessionId, token)
        } catch (err) {
          console.warn("Failed to clear collection scope (non-critical):", err)
        }

        setSessionRefreshTrigger((prev) => prev + 1)

        // Drop the deleted session's store slice (aborts any in-flight stream).
        store.resetSession(deletedSessionId)

        if (deletedSessionId === sessionId) {
          const newId = crypto.randomUUID()
          store.resetSession(newId)
          setSessionId(newId)
          setIsExistingSession(false)
          setIsSessionPersisted(false)
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to delete session"
        setPageError(errorMessage)
        console.error("Failed to delete session:", err)
      }
    },
    [
      sessionId,
      auth,
      store,
      setSessionRefreshTrigger,
      setSessionId,
      setIsExistingSession,
      setIsSessionPersisted,
      setPageError,
    ]
  )

  /** Whether the extractions panel is open (survey-variable-discovery only). */
  const [extractionsOpen, setExtractionsOpen] = useState<boolean>(false)

  /** Surface the extractions panel when a variable is added. */
  const handleExtractionAdded = useCallback((): void => {
    setExtractionsOpen(true)
  }, [])

  return (
    <>
      <title>{AGENT_WELCOME[agent] ? `${AGENT_WELCOME[agent].title} - MAP Apps Platform` : "MAP Apps Platform"}</title>
      <AgentChatLayout
        agent={agent}
        onNewChat={handleNewChat}
        canStartNewChat={canStartNewChat}
        activeSessionId={sessionId}
        onSelectSession={handleSelectSession}
        onDeleteSession={handleDeleteSession}
        onActiveSessionGone={() => {
          setPageError("This session is no longer available")
          handleSessionLoadFailed()
        }}
        sessionRefreshTrigger={sessionRefreshTrigger}
        extractionsOpen={extractionsOpen}
        onExtractionsToggle={() => setExtractionsOpen(!extractionsOpen)}
        isSessionPersisted={isSessionPersisted}
      >
        <ChatInterface
          sessionId={sessionId}
          isExistingSession={isExistingSession}
          onHasAssistantMessages={setCanStartNewChat}
          onExtractionAdded={handleExtractionAdded}
          agent={agent}
          externalError={configurationError ?? pageError}
          onClearExternalError={() => setPageError(null)}
        />
      </AgentChatLayout>
    </>
  )
}

/**
 * Chat page route component.
 *
 * Owns the active session id and session lifecycle flags, mounts the
 * agent-specific providers and the per-session {@link ChatSessionsProvider}
 * (which holds streaming/message state), then renders {@link ChatPageBody}
 * beneath the provider so lifecycle handlers can reset store slices.
 *
 * @param agent - The agent to run this chat page for.
 */
export default function ChatPage({
  agent = DEFAULT_AGENT_NAME,
}: ChatPageProps) {
  /** Error from session operations (delete, etc.) displayed in the chat area. */
  const [pageError, setPageError] = useState<string | null>(null)

  /** Persistent provider error; page actions must not hide an unusable client. */
  const [configurationError, setConfigurationError] = useState<string | null>(null)

  /** The active session ID, shared by the sidebar, store, and interface. */
  const [sessionId, setSessionId] = useState<string>(() => crypto.randomUUID())

  /**
   * Whether the current session was selected from the sidebar (triggers history
   * loading). Distinct from isSessionPersisted so persisting after the first
   * message does not re-trigger a history load.
   */
  const [isExistingSession, setIsExistingSession] = useState<boolean>(false)

  /**
   * Whether the current session exists in DynamoDB. Drives UI visibility (e.g.
   * the extractions panel) independently of the history-load trigger.
   */
  const [isSessionPersisted, setIsSessionPersisted] = useState<boolean>(false)

  /** Incremented each time a session is persisted to trigger a sidebar refresh. */
  const [sessionRefreshTrigger, setSessionRefreshTrigger] = useState<number>(0)

  /**
   * Called when the store fails to load a session's history — reset to a fresh
   * session. Passed to the provider so it can recover from a missing session.
   */
  const handleSessionLoadFailed = useCallback(() => {
    setSessionId(crypto.randomUUID())
    setIsExistingSession(false)
    setIsSessionPersisted(false)
  }, [])

  /** Called by the store after a session is persisted — refresh the sidebar. */
  const handleSessionPersisted = useCallback(() => {
    setSessionRefreshTrigger((prev) => prev + 1)
    setIsSessionPersisted(true)
  }, [])

  /** Called by the store to request a sidebar refresh. */
  const handleSidebarRefresh = useCallback(() => {
    setSessionRefreshTrigger((prev) => prev + 1)
  }, [])

  /**
   * Wrap the page tree with any agent-specific providers. Mounting is
   * unconditional per agent so providers persist across the
   * new-chat -> first-message transition without remounting the tree.
   *
   * @param children - The base page tree to wrap.
   * @returns The (possibly wrapped) page tree.
   */
  function withAgentProviders(children: React.ReactNode): React.ReactNode {
    switch (agent) {
      case "survey-variable-discovery":
        return (
          <ExtractionsProvider sessionId={sessionId} enabled={isSessionPersisted}>
            {children}
          </ExtractionsProvider>
        )
      case "deep-literature-review":
        return (
          <CollectionProvider sessionId={sessionId} isSessionPersisted={isSessionPersisted}>
            <DeepLiteratureMessagePresentationProvider>
              {children}
            </DeepLiteratureMessagePresentationProvider>
          </CollectionProvider>
        )
      case "exploratory-literature-review":
        return <ReportPanelProvider sessionId={sessionId}>{children}</ReportPanelProvider>
      default:
        return children
    }
  }

  return (
    <InvocationContextProvider>
      {withAgentProviders(
        <AgentCoreClientProvider activeAssistantId={agent} onError={setConfigurationError}>
          <ChatSessionsProvider
            agent={agent}
            activeSessionId={sessionId}
            onSessionPersisted={handleSessionPersisted}
            onSessionLoadFailed={handleSessionLoadFailed}
            onRefreshSidebar={handleSidebarRefresh}
          >
            <ChatPageBody
              agent={agent}
              sessionId={sessionId}
              setSessionId={setSessionId}
              isExistingSession={isExistingSession}
              setIsExistingSession={setIsExistingSession}
              isSessionPersisted={isSessionPersisted}
              setIsSessionPersisted={setIsSessionPersisted}
              setSessionRefreshTrigger={setSessionRefreshTrigger}
              sessionRefreshTrigger={sessionRefreshTrigger}
              pageError={pageError}
              setPageError={setPageError}
              configurationError={configurationError}
            />
          </ChatSessionsProvider>
        </AgentCoreClientProvider>
      )}
    </InvocationContextProvider>
  )
}
